import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import Logo from "components/Logo";
import { setDarkMode, setMiniSidenav, useMaterialUIController } from "context";
import Sidenav from "examples/Sidenav";
import SignInIllustration from "layouts/authentication/sign-in/illustration";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import routes from "routes";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import AutoLogout from "utils/AutoLogout";
import RecoverAccount from "views/account/RecoverAccount";
import Home from "views/home/Home";
import SystemWrapper from "views/SystemWrapper";
import MonitoriaSystem from "views/coordenador/monitoria/MonitoriaSystem";

export const MySwal = withReactContent(Swal);

// Ignora o erro benigno do ResizeObserver que causa a tela vermelha no React em desenvolvimento
window.addEventListener('error', e => {
  if (e.message === 'ResizeObserver loop limit exceeded' || e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
});

export default function App() {
  // Share session bridge for pop-out window
  const tempUser = localStorage.getItem('temp_share_user');
  if (tempUser) {
    sessionStorage.setItem('user', tempUser);
    localStorage.removeItem('temp_share_user');
  }
  const tempMenus = localStorage.getItem('temp_share_menus');
  if (tempMenus) {
    sessionStorage.setItem('userMenus', tempMenus);
    localStorage.removeItem('temp_share_menus');
  }
  const tempSelectedPages = localStorage.getItem('temp_share_selected_pages');
  if (tempSelectedPages) {
    sessionStorage.setItem('selectedPages', tempSelectedPages);
    localStorage.removeItem('temp_share_selected_pages');
  }

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, layout, sidenavColor, darkMode } = controller;

  const [authRoutes, setAuthRoutes] = useState([]);
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [selectedPages, setSelectedPages] = useState([{
    name: "Home",
    key: "home",
    route: "/pages/home",
    component: <Home />,
  }]);

  let pages = JSON.parse(sessionStorage.getItem('selectedPages')) || []
  let tema = sessionStorage.getItem('tema')

  const handleAuthRoutes = (userFunctionalities) => {
    const filterRoutes = (items) => {
      return items.reduce((acc, item) => {
        const { type, collapse, menu } = item;

        if (type === "divider") {
          acc.push(item);
          return acc;
        }

        // 1. Validar se o item tem código e se o usuário tem permissão
        if (menu && !userFunctionalities?.includes(menu)) {
          return acc;
        }

        // 2. Se for um item com colapso (categoria ou sub-menu)
        if (collapse && collapse.length > 0) {
          const filteredChildren = filterRoutes(collapse);
          
          // Se tiver filhos filtrados, mantém o pai
          if (filteredChildren.length > 0) {
            acc.push({ ...item, collapse: filteredChildren });
          } else if (menu) {
            // Se o pai tem código de menu mas os sub-itens foram filtrados,
            // podemos manter o pai vazio se ele for um link direto (como MEU PERFIL)
            acc.push({ ...item, collapse: [] });
          }
          return acc;
        }

        // 3. Item simples (sem menu próprio e sem filhos - herda do pai ou é público)
        acc.push(item);
        return acc;
      }, []);
    };

    const screens = filterRoutes(routes);
    setAuthRoutes(screens);
  };

  const findRouteByKey = (key, routes) => {
    for (const route of routes) {
      if (route.key === key) return route;
      if (route.collapse) {
        const found = findRouteByKey(key, route.collapse);
        if (found) return found;
      }
    }
    return null;
  };

  const isRouteAllowed = (key, allowedRoutes) => {
    if (key === 'home') return true;
    if (!allowedRoutes || allowedRoutes.length === 0) return false;
    for (const route of allowedRoutes) {
      if (route.key === key) return true;
      if (route.collapse && isRouteAllowed(key, route.collapse)) return true;
    }
    return false;
  };

  useEffect(() => {
    const storedSelectedPages = pages.map(key => findRouteByKey(key, routes)).filter(Boolean);

    // Mantem home sempre presente
    const homePage = {
      name: "Home",
      key: "home",
      route: "/pages/home",
      component: <Home />,
    };
    if (!storedSelectedPages.find(page => page.key === 'home')) {
      storedSelectedPages.unshift(homePage);
    }
    setSelectedPages(storedSelectedPages);
  }, []);

  useEffect(() => {
    if (authRoutes && authRoutes.length > 0) {
      setSelectedPages(prevPages => {
        const filtered = prevPages.filter(p => isRouteAllowed(p.key, authRoutes));
        if (!filtered.some(p => p.key === 'home')) {
          const homePage = {
            name: "Home",
            key: "home",
            route: "/pages/home",
            component: <Home />,
          };
          return [homePage, ...filtered];
        }
        return filtered;
      });
    }
  }, [authRoutes]);

  useEffect(() => {
    const keys = selectedPages.map(page => page.key);
    sessionStorage.setItem('selectedPages', JSON.stringify(keys));
  }, [selectedPages]);

  useEffect(() => {
    if (pages.length == 0) {
      setSelectedPages([{
        name: "Home",
        key: "home",
        route: "/pages/home",
        component: <Home />,
      }]);
    }
  }, [pages]);

  useEffect(() => {
    if (tema == 'false') {
      setDarkMode(dispatch, false)
    } else if (tema == 'true') {
      setDarkMode(dispatch, true)
    } else {
      setDarkMode(dispatch, false)
    }
  }, [tema]);

  const onPageSelect = route => {
    const existingPage = selectedPages.find(r => r.key === route.key);
    if (existingPage) {
      setSelectedPages(selectedPages.map(page =>
        page.key === route.key ? { ...page, active: true } : { ...page, active: false }
      ));
    } else {
      setSelectedPages(selectedPages.map(page => ({ ...page, active: false })).concat({ ...route, active: true }));
    }
  };

  const onClosePageSelected = route => {
    setSelectedPages(selectedPages.filter(tab => tab.key !== route.key));
  };

  const { pathname } = useLocation();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);


  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && !window.location.search.includes("view=monitoria") && (
        <Sidenav
          color={sidenavColor}
          routes={authRoutes}
          onPageSelect={onPageSelect}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Routes>
        <Route exact path="/login" element={<SignInIllustration />} />
        <Route exact path="/reset-password/:token" element={<RecoverAccount />} />
        <Route exact path="/images/logo.png" element={<Logo />} />
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="*" element={<Navigate to="/login" />} />
        <Route exact path="/system" element={
          <AutoLogout>
            {window.location.search.includes("view=monitoria") ? (
              <MonitoriaSystem standalone={true} />
            ) : (
              <SystemWrapper
                tabs={selectedPages}
                handleSidenavMenus={handleAuthRoutes}
                closeTab={onClosePageSelected}
                onPageSelect={onPageSelect}
              />
            )}
          </AutoLogout>
        } />
      </Routes>
    </ThemeProvider >
  );
}
